import { useApiMutation } from "./useApiMutation";
import { useApiQuery } from "./useApiQuery";
import { workoutService } from "../services/workoutService";
import type { WorkoutPayload, GetWorkoutsParams, GetProgramsParams } from "../types/workout.types";
import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { queryTags } from "../queryTags";
import { getQueryClient } from "../queryClient";

export function useCreateWorkout(
  options?: UseMutationOptions<any, any, WorkoutPayload, any>
) {
  return useApiMutation<any, any, WorkoutPayload, any>({
    mutationFn: ((data: WorkoutPayload) =>
      workoutService.createWorkout(data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout] });
    },
    ...options,
  });
}

export function useGetWorkouts(
  params?: GetWorkoutsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, "queryKey" | "queryFn">
) {
  return useApiQuery({
    queryKey: [
      queryTags.workout,
      params?.page || 1,
      params?.limit || 10,
      params?.search || "",
    ],
    queryFn: () => workoutService.getWorkouts(params),
    ...options,
  });
}

export function useGetWorkout(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, "queryKey" | "queryFn">
) {
  return useApiQuery({
    queryKey: [queryTags.workout, id],
    queryFn: () => workoutService.getWorkout(id),
    enabled: !!id,
    ...options,
  });
}

export function useUpdateWorkout(
  options?: UseMutationOptions<
    any,
    any,
    { id: string; data: WorkoutPayload },
    any
  >
) {
  return useApiMutation<any, any, { id: string; data: WorkoutPayload }, any>({
    mutationFn: ((variables: { id: string; data: WorkoutPayload }) =>
      workoutService.updateWorkout(variables.id, variables.data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout] });
    },
    ...options,
  });
}

export function useDeleteWorkout(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => workoutService.deleteWorkout(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout] });
    },
    ...options,
  });
}

export function useProgramAdd(
  options?: UseMutationOptions<any, any, any, any>
) {
  return useApiMutation<any, any, any, any>({
    mutationFn: ((data: any) => workoutService.addProgram(data)) as any,
    ...options,
  });
}

export function useGetPrograms(
  params?: GetProgramsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, "queryKey" | "queryFn">
) {
  return useApiQuery({
    queryKey: [
      queryTags.workout,
      "programs",
      params?.page || 1,
      params?.limit || 10,
      params?.search || "",
    ],
    queryFn: () => workoutService.getPrograms(params),
    ...options,
  });
}

export function useGetProgram(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, "queryKey" | "queryFn">
) {
  return useApiQuery({
    queryKey: [queryTags.workout, "program", id],
    queryFn: () => workoutService.getProgram(id),
    enabled: !!id,
    ...options,
  });
}

export function useGetWeeklyPrograms(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, "queryKey" | "queryFn">
) {
  return useApiQuery({
    queryKey: [queryTags.workout, "weeklyPrograms", id],
    queryFn: () => workoutService.getWeeklyPrograms(id),
    enabled: !!id,
    ...options,
  });
}

export function useUpdateProgram(
  options?: UseMutationOptions<any, any, { id: string; data: any }, any>
) {
  return useApiMutation<any, any, { id: string; data: any }, any>({
    mutationFn: ((variables: { id: string; data: any }) =>
      workoutService.updateProgram(variables.id, variables.data)) as any,
    onSuccess: () => {
      // Refresh all program-related queries (lists and details)
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout] });
    },
    ...options,
  });
}

export function useDeleteProgram(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => workoutService.deleteProgram(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout] });
    },
    ...options,
  });
}

export function useUpdateWeeklyPrograms(
  options?: UseMutationOptions<any, any, { id: string; data: any }, any>
) {
  return useApiMutation<any, any, { id: string; data: any }, any>({
    mutationFn: ((variables: { id: string; data: any }) =>
      workoutService.updateWeeklyPrograms(variables.id, variables.data)) as any,
    ...options,
  });
}

export function useDeleteWeeklyProgram(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => workoutService.deleteWeeklyProgram(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout, "weeklyPrograms"] });
    },
    ...options,
  });
}

export function useGetRefWorkouts(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, "queryKey" | "queryFn">
) {
  return useApiQuery({
    queryKey: [queryTags.workout, "refWorkouts", id],
    queryFn: () => workoutService.getRefWorkouts(id),
    enabled: !!id,
    ...options,
  });
}
